import React, { useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useStore } from '../context/StoreContext';
import { usePermissions } from '../context/PermissionContext';
import { ROUTE_MODULE_MAP } from '../config/routeModules';

// ─── PrivateRoute ────────────────────────────────────────────────────────────
// Layer 1: authentication + store selection + module availability
const PrivateRoute = ({ children, requireStore = false }) => {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const { selectedStore, loading: storeLoading, modulesEnabled } = useStore();
  const location = useLocation();

  if (authLoading || (requireStore && storeLoading)) {
    return (
      <div className="loading">
        <div className="spinner" />
        <p>Loading...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (requireStore && !selectedStore) {
    return <Navigate to="/select-store" replace />;
  }

  // Module-key check: redirect if store has explicitly disabled this route's module
  const rootPath = `/${location.pathname.split('/').filter(Boolean)[0] || ''}`;
  const moduleKey = ROUTE_MODULE_MAP[location.pathname] || ROUTE_MODULE_MAP[rootPath];
  if (moduleKey && modulesEnabled?.[moduleKey] === false) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

export default PrivateRoute;


// ─── PermissionGate ──────────────────────────────────────────────────────────
// Layer 2: role-based permission check for a page or section
export const PermissionGate = ({ permission, children, fallback = null }) => {
  const { hasPermission, loading } = usePermissions();

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner" />
      </div>
    );
  }

  if (permission && !hasPermission(permission)) {
    return fallback || (
      <div style={{ padding: '3rem', textAlign: 'center' }}>
        <h2 style={{ color: '#1e293b' }}>Access Denied</h2>
        <p style={{ color: '#64748b' }}>You don&apos;t have permission to view this page.</p>
      </div>
    );
  }

  return children;
};


// ─── TierGate ────────────────────────────────────────────────────────────────
// Layer 2b: subscription tier check for enterprise-only features
// Usage: <TierGate tier="enterprise"><ApprovalsPage /></TierGate>
export const TierGate = ({ tier, children }) => {
  const { hasTier } = usePermissions();
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);

  if (!hasTier(tier)) {
    return (
      <div style={{ padding: '3rem', textAlign: 'center' }}>
        <div style={{ fontSize: 40, marginBottom: 12 }}>🔒</div>
        <h2 style={{ color: '#1e293b', margin: '0 0 8px' }}>Enterprise Feature</h2>
        <p style={{ color: '#64748b', margin: '0 0 24px' }}>
          This feature is available on the Enterprise plan.
        </p>
        <button className="btn btn-primary" onClick={() => setShowModal(true)}>
          View Plans
        </button>

        {showModal && (
          <div style={{
            position: 'fixed', inset: 0,
            background: 'rgba(15, 23, 42, 0.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1000,
          }}>
            <div style={{
              background: '#fff', borderRadius: 16,
              width: '100%', maxWidth: 440, padding: 32, textAlign: 'left',
            }}>
              <h3 style={{ margin: '0 0 8px', color: '#1e293b' }}>Unlock {tier} features</h3>
              <p style={{ margin: '0 0 20px', color: '#64748b' }}>
                Upgrade your plan to access advanced approval workflows,
                audit controls, and enterprise RBAC.
              </p>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button className="btn btn-outline" onClick={() => setShowModal(false)}>
                  Later
                </button>
                <button className="btn btn-primary" onClick={() => navigate('/billing')}>
                  Upgrade Plan
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return children;
};


// ─── ModuleGate ──────────────────────────────────────────────────────────────
// Blocks page access if the store's modules_enabled dict sets key = false.
// Only hides — does not render upgrade prompt (use TierGate for that).
export const ModuleGate = ({ module: moduleName, children }) => {
  const { modulesEnabled } = useStore();
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);

  if (modulesEnabled && modulesEnabled[moduleName] === false) {
    return (
      <div style={{ padding: '3rem', textAlign: 'center' }}>
        <h2 style={{ color: '#1e293b' }}>Feature not available</h2>
        <p style={{ color: '#64748b' }}>
          This module is not enabled for your store.
        </p>
        <button className="btn btn-primary" style={{ marginTop: '1rem' }}
          onClick={() => setShowModal(true)}>
          Upgrade Plan
        </button>
        {showModal && (
          <div style={{
            position: 'fixed', inset: 0,
            background: 'rgba(15, 23, 42, 0.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1000,
          }}>
            <div style={{
              background: '#fff', borderRadius: 12,
              width: '100%', maxWidth: 480, padding: 24, textAlign: 'left',
            }}>
              <h3 style={{ marginTop: 0, marginBottom: 8, color: '#1e293b' }}>
                Unlock {moduleName}
              </h3>
              <p style={{ marginTop: 0, color: '#64748b' }}>
                Upgrade your plan to access this module.
              </p>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button className="btn btn-outline" onClick={() => setShowModal(false)}>
                  Maybe Later
                </button>
                <button className="btn btn-primary" onClick={() => navigate('/billing')}>
                  Upgrade Plan
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return children;
};


// ─── FeatureLock ─────────────────────────────────────────────────────────────
// Layer 3: inline component-level lock (button, field, action)
// Usage: <FeatureLock permission="edit_product"><EditButton /></FeatureLock>
//        <FeatureLock tier="enterprise"><ApproveButton /></FeatureLock>
export const FeatureLock = ({ permission, tier, children, tooltip }) => {
  const { hasPermission, hasTier } = usePermissions();

  const allowed = (
    (!permission || hasPermission(permission)) &&
    (!tier || hasTier(tier))
  );

  if (!allowed) {
    return (
      <span
        title={tooltip || 'You do not have permission for this action'}
        style={{ cursor: 'not-allowed', opacity: 0.4, display: 'inline-block' }}
        onClick={e => e.stopPropagation()}
      >
        {children}
      </span>
    );
  }

  return children;
};
